import clsx from 'clsx';
import React from 'react';

import AutocompleteField from '@components/Form/AutocompleteField';
import DropdownField from '@components/Form/DropdownField';
import TextField from '@components/Form/TextField';
import { useEntitiesCreate } from '@hooks';
import { autocompleteEntities } from '@utils/backend-api/entities';
import { ENTITY_TYPE_ORGANIZATION } from '@utils/backend-api/profileSettings';
import { COMPANY_SIZE_OPTIONS } from '@utils/backend-api/profileSettings';
import checkForExistenceInArray from '@utils/checkForExistenceInArray';

import IndustryFields from './IndustryFields';
import JobFunctionFields from './JobFunctionFields';
import JobLevelFields from './JobLevelFields';
import SubIndustryFields from './SubIndustryFields';
import SubJobFunctionFields from './SubJobFunctionFields';

interface FirstPartyDataFieldsProps {
  dataFields?: string[];
  isSetting?: boolean;
  isInterruptedOnboarding?: boolean;
  optionalFirstPartyFields?: string[];
  silenceToast?: boolean;
}

const makeRequiredLabel = (label: string, isSetting: boolean): string => {
  return isSetting ? label : `${label}*`;
};

const FirstPartyDataFields = ({
  dataFields,
  isSetting = false,
  isInterruptedOnboarding = false,
  optionalFirstPartyFields = [],
  silenceToast = false,
}: FirstPartyDataFieldsProps) => {
  const { mutateAsync: createEntityAsync } = useEntitiesCreate(silenceToast);

  const checkIfRequired = (fieldItem: string, isRequired: boolean) =>
    optionalFirstPartyFields.includes(fieldItem) ? false : isRequired;

  return (
    <>
      {!isSetting ? (
        isInterruptedOnboarding ? (
          // Interrupted onboarding layout (paywall flow): jobLevel and Industry in same row, Organization below
          <>
            <div className="flex gap-8">
              <div className="flex-1">
                {checkForExistenceInArray({ arrayData: dataFields, phrase: 'jobLevel' }) && (
                  <JobLevelFields
                    isRequired={checkIfRequired('jobLevel', !isSetting)}
                    silenceToast={silenceToast}
                  />
                )}
              </div>
              <div className="flex-1">
                {checkForExistenceInArray({
                  arrayData: dataFields,
                  phrase: 'organizationIndustry',
                }) && (
                  <IndustryFields
                    isRequired={checkIfRequired('organizationIndustry', !isSetting)}
                    silenceToast={silenceToast}
                  />
                )}
              </div>
            </div>
            {checkForExistenceInArray({ arrayData: dataFields, phrase: 'companyName' }) && (
              <div className="mt-24">
                <AutocompleteField
                  label={makeRequiredLabel('Organization', true)}
                  name="company"
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      entity_type: ENTITY_TYPE_ORGANIZATION,
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: ENTITY_TYPE_ORGANIZATION,
                      word_type: 'proper',
                    })
                  }
                  placeholder="Type to search organizations…"
                />
              </div>
            )}
          </>
        ) : (
          // Standard onboarding layout (promo flow)
          <>
            {checkForExistenceInArray({ arrayData: dataFields, phrase: 'jobLevel' }) && (
              <JobLevelFields
                isRequired={checkIfRequired('jobLevel', !isSetting)}
                silenceToast={silenceToast}
              />
            )}
            <div className="mt-24 flex gap-8">
              <div className="flex-1">
                {checkForExistenceInArray({ arrayData: dataFields, phrase: 'jobFunction' }) && (
                  <JobFunctionFields
                    isRequired={checkIfRequired('jobFunction', !isSetting)}
                    silenceToast={silenceToast}
                  />
                )}
              </div>
              <div className="flex-1">
                {checkForExistenceInArray({ arrayData: dataFields, phrase: 'subJobFunction' }) && (
                  <SubJobFunctionFields
                    isRequired={checkIfRequired('subJobFunction', !isSetting)}
                    silenceToast={silenceToast}
                  />
                )}
              </div>
            </div>
            {checkForExistenceInArray({ arrayData: dataFields, phrase: 'companyName' }) && (
              <div className="mt-24">
                <AutocompleteField
                  label={makeRequiredLabel('Organization', isSetting)}
                  name="company"
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      entity_type: ENTITY_TYPE_ORGANIZATION,
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: ENTITY_TYPE_ORGANIZATION,
                      word_type: 'proper',
                    })
                  }
                  placeholder="Type to search organizations…"
                />
              </div>
            )}
            <div className="mt-24 flex gap-8">
              <div className="flex-1">
                {checkForExistenceInArray({
                  arrayData: dataFields,
                  phrase: 'organizationIndustry',
                }) && (
                  <IndustryFields
                    isRequired={checkIfRequired('organizationIndustry', !isSetting)}
                    silenceToast={silenceToast}
                  />
                )}
              </div>
              <div className="flex-1">
                {checkForExistenceInArray({
                  arrayData: dataFields,
                  phrase: 'organizationSubIndustry',
                }) && (
                  <SubIndustryFields
                    isRequired={checkIfRequired('organizationSubIndustry', !isSetting)}
                    silenceToast={silenceToast}
                  />
                )}
              </div>
            </div>

            <div className={'mt-24 flex flex-wrap gap-8'}>
              <div className="flex-1">
                {checkForExistenceInArray({ arrayData: dataFields, phrase: 'companySize' }) && (
                  <DropdownField
                    name="companySize"
                    options={COMPANY_SIZE_OPTIONS}
                    label="Company Size"
                    placeholder="Select"
                    isClearable={false}
                    scrollIntoView
                  />
                )}
              </div>
              <div className="flex-1">
                {checkForExistenceInArray({ arrayData: dataFields, phrase: 'postalCode' }) && (
                  <TextField label="Your Postal Code" name="postalCode" />
                )}
              </div>
            </div>
          </>
        )
      ) : (
        <>
          {checkForExistenceInArray({ arrayData: dataFields, phrase: 'jobLevel' }) && (
            <JobLevelFields
              isRequired={checkIfRequired('jobLevel', !isSetting)}
              silenceToast={silenceToast}
            />
          )}
          {checkForExistenceInArray({ arrayData: dataFields, phrase: 'jobFunction' }) && (
            <JobFunctionFields
              isRequired={checkIfRequired('jobFunction', !isSetting)}
              silenceToast={silenceToast}
            />
          )}
          {checkForExistenceInArray({ arrayData: dataFields, phrase: 'subJobFunction' }) && (
            <SubJobFunctionFields
              isRequired={checkIfRequired('subJobFunction', !isSetting)}
              silenceToast={silenceToast}
            />
          )}
          {checkForExistenceInArray({ arrayData: dataFields, phrase: 'companyName' }) && (
            <div className="mt-24">
              <AutocompleteField
                label={makeRequiredLabel('Organization', isSetting)}
                name="company"
                fetchMethod={(value) =>
                  autocompleteEntities({
                    fragment: value,
                    entity_type: ENTITY_TYPE_ORGANIZATION,
                  })
                }
                createMethod={(value) =>
                  createEntityAsync({
                    name: value,
                    entity_type: ENTITY_TYPE_ORGANIZATION,
                    word_type: 'proper',
                  })
                }
                placeholder="Type to search organizations…"
              />
            </div>
          )}

          {checkForExistenceInArray({ arrayData: dataFields, phrase: 'organizationIndustry' }) && (
            <IndustryFields
              isRequired={checkIfRequired('organizationIndustry', !isSetting)}
              silenceToast={silenceToast}
            />
          )}
          {checkForExistenceInArray({
            arrayData: dataFields,
            phrase: 'organizationSubIndustry',
          }) && (
            <SubIndustryFields
              isRequired={checkIfRequired('organizationSubIndustry', !isSetting)}
              silenceToast={silenceToast}
            />
          )}
          <div className={clsx({ 'flex flex-wrap gap-16 sm:flex-nowrap': isSetting })}>
            {checkForExistenceInArray({ arrayData: dataFields, phrase: 'companySize' }) && (
              <DropdownField
                name="companySize"
                options={COMPANY_SIZE_OPTIONS}
                label="Company Size"
                placeholder="Select"
                isClearable={false}
                className={clsx({ 'w-full sm:w-3/6': isSetting })}
                scrollIntoView
              />
            )}
            {checkForExistenceInArray({ arrayData: dataFields, phrase: 'postalCode' }) && (
              <div
                className={clsx({
                  'min-w-full sm:mt-24 sm:min-w-[350px] md:min-w-[250px] lg:min-w-[300px]':
                    isSetting,
                  'mt-24': !isSetting,
                })}
              >
                <TextField
                  name="postalCode"
                  inputClassName="dark:border-white dark:border-solid"
                  label="Your Postal Code"
                />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default FirstPartyDataFields;
